import { useAppStore } from "@/hooks/useAppStore"
import { Panel, Stat, Badge, SectionTitle } from "@/components/ui"
import { Trajectory3D } from "@/components/viewer/Trajectory3D"
import { TelemetryReadout } from "@/components/ui/TelemetryReadout"
import { SimCharts } from "@/components/charts/TelemetryCharts"
import { formatTime } from "@/lib/format"

export default function TelemetryPage() {
  const { simStatus, simCurrent, simTelemetry, simMetrics } = useAppStore()
  const hasSim = simTelemetry && simTelemetry.length > 0

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Telemetry</h1>
          <p className="text-muted text-sm">
            Live flight instrumentation from the client-side simulation engine
          </p>
        </div>
        <Badge variant={simStatus === "running" ? "running" : simStatus === "completed" ? "nominal" : "muted"}>
          {simStatus.toUpperCase()}
        </Badge>
      </div>

      <SectionTitle>Instrument panel</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
        <Stat label="MET" value={formatTime(simCurrent?.t ?? 0)} unit="" status="nominal" accent="cyan" />
        <Stat label="Altitude" value={simCurrent?.altitude?.toFixed(0) ?? "—"} unit="m" status="nominal" accent="cyan" />
        <Stat label="Speed" value={simCurrent?.speed?.toFixed(1) ?? "—"} unit="m/s" status="nominal" accent="blue" />
        <Stat label="Fuel" value={simCurrent?.fuel?.toFixed(0) ?? "—"} unit="kg" status={simCurrent && simCurrent.fuel < 20 ? "warn" : "nominal"} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-8 xl:col-span-9 gap-4">
          <Panel title="Trajectory (3D)" padded={false}>
            <div className="p-3">
              <Trajectory3D
                points={simPoints(simTelemetry)}
                height={520}
                playing={simStatus === "running"}
              />
            </div>
          </Panel>
          <Panel title="Charts" padded={false}>
            <div className="p-3">
              {hasSim ? (
                <SimCharts telemetry={simTelemetry as any} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 h-[520px] flex items-center justify-center text-muted text-sm">
                  No live telemetry yet. Start a simulation in Flight Simulation to stream data.
                </div>
              )}
            </div>
          </Panel>
        </div>

        <div className="lg:col-span-4 xl:col-span-3 gap-4">
          <Panel title="Readout" padded={false}>
            <div className="p-3">
              <TelemetryReadout reading={simCurrent ?? null} />
              {!simCurrent && (
                <div className="mt-3 h-10 flex items-center justify-center text-muted text-sm">
                  Waiting for simulation data&hellip;
                </div>
              )}
            </div>
          </Panel>
          {simMetrics && hasSim && (
            <Panel title="Flight outcome" padded={false}>
              <div className="p-3 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Result</span>
                  <Badge variant={simMetrics.landingSuccess ? "safe" : "danger"}>
                    {simMetrics.landingSuccess ? "LANDED" : "IMPACT"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Touchdown velocity</span>
                  <span className="font-mono text-ink">{simMetrics.touchdownVelocity?.toFixed(1) ?? "—"} m/s</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Horizontal error</span>
                  <span className="font-mono text-ink">{simMetrics.horizontalError?.toFixed(1) ?? "—"} m</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Fuel remaining</span>
                  <span className="font-mono text-ink">{simMetrics.fuelRemaining?.toFixed(0) ?? "—"} kg</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted">Duration</span>
                  <span className="font-mono text-ink">{simMetrics.duration?.toFixed(1) ?? "—"} s</span>
                </div>
              </div>
            </Panel>
          )}
          {!hasSim && (
            <Panel title="Note" padded={false}>
              <div className="p-3 text-sm text-muted leading-relaxed">
                Telemetry is produced by the live simulation engine only. Historical run
                telemetry streams are not exposed by the connected backend. Use Flight
                Simulation to generate and inspect flight data.
              </div>
            </Panel>
          )}
        </div>
      </div>
    </div>
  )
}

function simPoints(telemetry: any[] | null) {
  if (!telemetry || telemetry.length === 0) return []
  return telemetry.map((p) => ({
    t: p.t,
    x: p.x,
    z: p.y,
    y: p.altitude,
    speed: p.speed,
    fuel: p.fuel,
    throttle: p.throttle,
    altitude: p.altitude,
    vx: p.vx,
    vy: p.vy,
    vz: p.vz,
    tilt: p.tilt,
  }))
}
